import React from 'react';
import {
  CardPlace,
  placeLabelOf,
  regimeClassOf,
  WhatIfPayload,
  whatIfViewOf,
  WhatIfSpecRow,
} from 'components/ExportDesign/exportDesignChangeCardsData';

// --- A what-if as a before-and-after card (UX slice S7, board 09): the regime before and after, the specs
// side by side with the changed ones marked, and a "go back" that sends a what-if with the old values (it
// restores them the normal way: a what-if commits its spec). Drawn above the full result card of the new
// classification, in place of its markdown before/after lines. Only <div>s inside (bubble `p` rule).

interface ExportDesignWhatIfCardProps {
  card: WhatIfPayload;
  /** Sends a message as if typed (the "go back" what-if). */
  onSend?: (text: string) => void;
  disabled?: boolean;
}

const Column: React.FC<{ title: string; place: CardPlace; rows: WhatIfSpecRow[]; side: 'before' | 'after' }> = ({
  title,
  place,
  rows,
  side,
}) => (
  <div className={`export-design-diff-col${side === 'after' ? ' is-after' : ''}`}>
    <div className='export-design-diff-h'>{title}</div>
    <span className={`export-design-pill ${regimeClassOf(place.regime)} export-design-diff-pill`}>
      {placeLabelOf(place)}
    </span>
    {rows.map((row) => (
      <div
        key={row.key}
        className={`export-design-diff-row${row.changed && side === 'after' ? ' is-changed' : ''}`}
      >
        <span>{row.label}</span>
        <span className='export-design-code'>{(side === 'before' ? row.before : row.after) ?? '—'}</span>
      </div>
    ))}
  </div>
);

const ExportDesignWhatIfCard: React.FC<ExportDesignWhatIfCardProps> = ({ card, onSend, disabled }) => {
  const view = whatIfViewOf(card);
  return (
    <article className='export-design-block export-design-whatif' aria-label='What-if result'>
      <div className='export-design-block-h'>
        <div>
          <div className='export-design-block-kicker'>What-if</div>
          <h3 className='export-design-block-title'>{view.title}</h3>
          <div className='export-design-block-sub'>{view.sub}</div>
        </div>
        <div className='export-design-block-pills'>
          <span className={`export-design-pill ${card.moved ? 'is-ok' : 'is-neutral'}`}>
            {card.moved ? 'Moves it' : 'No change'}
          </span>
        </div>
      </div>
      <div className='export-design-diff'>
        <Column title='Before' place={card.before} rows={card.specs} side='before' />
        <Column title='After' place={card.after} rows={card.specs} side='after' />
      </div>
      {card.more_specs > 0 && (
        <div className='export-design-diff-more'>
          and {card.more_specs} more spec{card.more_specs === 1 ? '' : 's'}, unchanged
        </div>
      )}
      {onSend && card.go_back_prompt && view.goBackLabel && (
        <div>
          <button
            type='button'
            className='standard-workflow-secondary-btn export-design-btn-sm'
            onClick={() => onSend(card.go_back_prompt as string)}
            disabled={disabled}
          >
            {view.goBackLabel}
          </button>
        </div>
      )}
    </article>
  );
};

export default ExportDesignWhatIfCard;
